"use client";
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export type ColorMode = "light" | "dark" | "system";
export type ResolvedTheme = "light" | "dark";

const STORAGE_KEY = "repo-color-mode";
const MEDIA_QUERY = "(prefers-color-scheme: dark)";

export interface ColorModeContextValue {
  colorMode: ColorMode;
  resolvedTheme: ResolvedTheme;
  setColorMode: (mode: ColorMode) => void;
  toggleColorMode: () => void;
}

const ColorModeContext = createContext<ColorModeContextValue | undefined>(undefined);

function getSystemTheme(): ResolvedTheme {
  if (typeof window === "undefined" || !window.matchMedia) {
    return "light";
  }
  return window.matchMedia(MEDIA_QUERY).matches ? "dark" : "light";
}

function resolveTheme(mode: ColorMode): ResolvedTheme {
  return mode === "system" ? getSystemTheme() : mode;
}

function readStoredColorMode(fallback: ColorMode): ColorMode {
  if (typeof window === "undefined") {
    return fallback;
  }
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage may be unavailable (private mode, SSR, etc.)
  }
  return fallback;
}

function applyTheme(theme: ResolvedTheme): void {
  if (typeof document === "undefined") {
    return;
  }
  document.documentElement.classList.toggle("dark", theme === "dark");
}

export interface ColorModeProviderProps {
  children: ReactNode;
  defaultColorMode?: ColorMode;
}

export function ColorModeProvider(props: ColorModeProviderProps) {
  const { children, defaultColorMode = "system" } = props;

  const [colorMode, setColorModeState] = useState<ColorMode>(defaultColorMode);
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() =>
    resolveTheme(defaultColorMode),
  );

  // Read the persisted/initial value on mount (guard for SSR / no-window).
  useEffect(() => {
    const initial = readStoredColorMode(defaultColorMode);
    setColorModeState(initial);
    setResolvedTheme(resolveTheme(initial));
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Apply the resolved theme to the root element whenever it changes.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Keep 'system' mode in sync with the OS preference.
  useEffect(() => {
    if (colorMode !== "system") {
      return;
    }
    if (typeof window === "undefined" || !window.matchMedia) {
      return;
    }
    const mql = window.matchMedia(MEDIA_QUERY);
    const onChange = () => setResolvedTheme(getSystemTheme());
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [colorMode]);

  const setColorMode = useCallback((mode: ColorMode) => {
    setColorModeState(mode);
    setResolvedTheme(resolveTheme(mode));
    if (typeof window !== "undefined") {
      try {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // ignore persistence failures
      }
    }
  }, []);

  const toggleColorMode = useCallback(() => {
    setColorMode(resolvedTheme === "dark" ? "light" : "dark");
  }, [resolvedTheme, setColorMode]);

  const value = useMemo<ColorModeContextValue>(
    () => ({ colorMode, resolvedTheme, setColorMode, toggleColorMode }),
    [colorMode, resolvedTheme, setColorMode, toggleColorMode],
  );

  return <ColorModeContext.Provider value={value}>{children}</ColorModeContext.Provider>;
}

export function useColorMode(): ColorModeContextValue {
  const context = useContext(ColorModeContext);
  if (context === undefined) {
    throw new Error("useColorMode must be used within a ColorModeProvider");
  }
  return context;
}
